import { invokeEdgeJson } from './queries/_edge'
import { MAILERLITE_SUBSCRIBE } from './queries/_endpoints'

/**
 * Semantic intents accepted by the mailerlite-subscribe edge function.
 * The FE no longer ships MailerLite group IDs — the edge function maps
 * intent → group ID server-side (2026-04-28). Future MailerLite group
 * changes are a 1-file BE edit instead of touching every form.
 *
 * Lifecycle groups (premium) are not represented here — those fire only
 * from stripe-webhook server-side after a real payment event
 * (Codex audit fix #5, 2026-04-21).
 *
 * Intents:
 *   NEWSLETTER — general email opt-ins (anonymous). Used by the public
 *                landing-page newsletter form, blog newsletter CTA, and
 *                the Donate page.
 *   SIGN_UP    — authenticated free-account signups; triggers the Free
 *                Welcome Drip. Requires a valid bearer token to prevent
 *                spam-adding fake emails to the drip audience.
 *
 * The legacy WAITLIST intent was removed on 2026-05-18 — soft-launch had
 * retired the framing on 2026-04-28, the form (WaitlistForm.jsx) was already
 * deleted, and the edge function's waitlist code path was dead.
 */
export const MAILERLITE_INTENTS = {
  NEWSLETTER: 'newsletter',
  SIGN_UP: 'signup',
}

// Intents that don't require an authenticated session (public forms).
// SIGN_UP is omitted intentionally — it represents an actual app signup
// and must come from an authenticated client.
const PUBLIC_INTENTS = new Set([MAILERLITE_INTENTS.NEWSLETTER])

/**
 * Subscribe an email to a MailerLite group via the mailerlite-subscribe edge
 * function. Throws on failure so callers (newsletter form, donate page, etc.)
 * can surface the actual reason — previously this swallowed all errors and
 * returned `false`, making real failures look like silent no-ops.
 *
 * Auth is auto-determined from the intent: NEWSLETTER is public (no bearer
 * token), SIGN_UP requires authentication.
 *
 * @param {string} email
 * @param {string} name
 * @param {string} intent - 'newsletter' | 'signup' (use MAILERLITE_INTENTS)
 * @returns {Promise<Object>} the edge function's response payload (typically `{ ok: true }`)
 */
export async function subscribeToMailerLite(email, name, intent) {
  return invokeEdgeJson(
    MAILERLITE_SUBSCRIBE,
    { email, name, intent },
    {
      auth: PUBLIC_INTENTS.has(intent) ? 'none' : 'required',
      errorMessage: 'Subscription failed — please try again',
    }
  )
}
